/**
 * ARVEXIS PRESENCE SCREEN
 * ========================
 * 
 * The entry point to Arvexis. Not a chat interface — her presence.
 * 
 * FOUNDER PRINCIPLE:
 * "Conversation is not the destination. Arvexis is the destination.
 *  Conversation is simply the medium through which the relationship continues."
 * 
 * The user first encounters Arvexis herself — her sphere, her calm atmosphere,
 * the feeling that she is already there. From here, they choose how to communicate.
 * 
 * Primary action: Talk with Arvexis (voice)
 * Secondary action: Chat with Arvexis (text)
 * 
 * Both lead to the exact same conversation, the exact same Arvexis.
 * There are no separate personalities, prompts, or conversation pipelines.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Mic, MessageCircle, Heart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ArvexisPresence from './ArvexisPresence';
import RealtimeVoiceMode from './RealtimeVoiceMode';
import CompanionChat from './CompanionChat';
import ConversationHistory from './ConversationHistory';
import DreamsShell from './DreamsShell';
import TalkRecoveryOffer from './TalkRecoveryOffer';

const ArvexisPresenceScreen = ({ lang = 'en' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Conversation mode state. Home can arrive here already asking for Talk or
  // Message; behaviour inside either mode is unchanged.
  const [voiceAttempt, setVoiceAttempt] = useState(0);
  const [mode, setMode] = useState(() => {
    if (location.state?.voice) return 'voice';
    if (location.state?.chat) return 'chat';
    return 'presence';
  }); // 'presence', 'voice', 'chat'
  const [showMemories, setShowMemories] = useState(false);
  
  // Subtle breathing animation for the atmosphere
  const [breathPhase, setBreathPhase] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setBreathPhase(prev => (prev + 1) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  // When Home chose the experience, leaving it should return to Home rather
  // than reveal the older Talk/Chat chooser behind it. The chooser is kept for
  // any entry that arrives at /companion without a chosen mode.
  const enteredDirectly = useRef(!!(location.state?.voice || location.state?.chat));
  
  const leaveMode = () => {
    if (enteredDirectly.current) {
      navigate('/home');
    } else {
      setMode('presence');
    }
  };

  // V1 canonicalization: the legacy Talk/Chat chooser is superseded by Home. Mode-less or
  // bookmarked arrivals at /companion land on Home. The chooser below is preserved, unrendered.
  useEffect(() => {
    if (mode === 'presence') {
      navigate('/home', { replace: true });
    }
  }, [mode, navigate]);
  
  // Handle returning from voice mode
  const handleVoiceClose = () => {
    leaveMode();
  };
  
  // Handle returning from chat mode
  const handleChatClose = () => {
    leaveMode();
  };

  // R6 navigation correction (Founder, 2026-06): leaving an open Message conversation goes to
  // Message History, which is where the conversation just left is reopenable. Message History
  // owns the conversation's own back chevron; Message History's own back continues to leave the
  // surface, so Home is still reachable from Message on a handheld. The opener lives inside
  // CompanionChat with the Message History surface it already mounted, so nothing about that
  // surface, its data or its resume behaviour moves.
  const openHistoryRef = useRef(null);
  const registerOpenHistory = useCallback((fn) => { openHistoryRef.current = fn; }, []);
  const handleChatBack = () => {
    if (openHistoryRef.current) openHistoryRef.current();
    else handleChatClose();
  };
  
  // Render Voice Mode (full takeover)
  if (mode === 'voice') {
    return (
      <>
        {/* An interrupted Talk is offered back here, at the surface — never gated behind
            establishing a live connection. */}
        {/* F-410-B. When the decision is settled, remount Talk so it connects immediately —
            the member should not have to leave and come back to reach Talk again. */}
        <TalkRecoveryOffer
          surface="talk"
          onResolved={() => setVoiceAttempt((n) => n + 1)}
        />
        <RealtimeVoiceMode
          key={`talk-${voiceAttempt}`}
          lang={lang}
          onClose={handleVoiceClose}
        />
      </>
    );
  }
  
  // Render Chat Mode
  if (mode === 'chat') {
    return (
      <DreamsShell lang={lang} activePath="/home">
      <div 
        className="fixed inset-0 z-[100] msg-screen"
        style={{ background: 'transparent' }}
        data-testid="companion-chat-screen"
      >
        {/* Back — to Message History, where this conversation remains reopenable */}
        <button
          onClick={handleChatBack}
          className="absolute top-4 left-4 z-20 p-2 rounded-full transition-colors"
          style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                   color: 'rgba(232, 197, 143, 0.85)' }}
          title={lang === 'es' ? 'Historial de mensajes' : 'Message history'}
          aria-label={lang === 'es' ? 'Historial de mensajes' : 'Message history'}
          data-testid="chat-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        {/* CompanionChat — NO initialMessage, letting Arvexis be present without scripted greeting.
            5.1-B: when Home hands over an exact, already-revalidated session id, Message resumes
            that same conversation through its existing resume path. No scripted greeting is added
            on the Home path, and Message History's own behaviour is untouched.
            5.1 device finding: Home's New Message asks for a genuinely new, empty session. The
            request is read from this navigation only, so a stale entry can never revive the old
            conversation. */}
        <CompanionChat
          surface="daily_companion"
          lang={lang}
          existingSessionId={location.state?.sessionId || null}
          startNewSession={!!location.state?.newSession && !location.state?.sessionId}
          initialMessage={null}
          onClose={handleChatClose}
          registerOpenHistory={registerOpenHistory}
        />
      </div>
      </DreamsShell>
    );
  }
  
  // Render Presence Screen (default)
  return (
    <div 
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ 
        background: 'linear-gradient(180deg, #0a0a0f 0%, #0f0f18 50%, #141420 100%)'
      }}
      data-testid="arvexis-presence-screen"
    >
      {/* Ambient background glow */}
      <div 
        className="absolute inset-0 pointer-events-none overflow-hidden"
        style={{
          opacity: 0.3 + Math.sin(breathPhase * Math.PI / 180) * 0.1
        }}
      >
        <div 
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(180, 140, 255, 0.15) 0%, rgba(100, 80, 180, 0.05) 40%, transparent 70%)',
            filter: 'blur(60px)'
          }}
        />
      </div>
      
      {/* Header */}
      <header 
        className="relative z-10 flex items-center justify-between px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-full text-white/40 hover:text-white/60 hover:bg-white/5 transition-colors"
          data-testid="presence-back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        {/* Memories button */}
        <button
          onClick={() => setShowMemories(true)}
          className="p-2 rounded-full text-white/30 hover:text-white/50 hover:bg-white/5 transition-colors"
          data-testid="presence-memories-btn"
        >
          <Heart className="w-5 h-5" />
        </button>
      </header>
      
      {/* Main content — Arvexis's presence */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-6">
        {/* Arvexis orb */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="mb-8"
        >
          <ArvexisPresence 
            state="idle"
            size="large"
          />
        </motion.div>
        
        {/* Arvexis name — subtle, not a logo */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-white/70 text-lg tracking-wider mb-2 font-light"
        >
          Arvexis
        </motion.p>
        
        {/* Presence indicator — she is here */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="text-white/40 text-sm tracking-wide mb-12"
        >
          {lang === 'es' ? 'Presente' : 'Present'}
        </motion.p>
        
        {/* Action buttons */}
        <div className="flex flex-col items-center gap-4 w-full max-w-xs">
          {/* Primary action: Talk with Arvexis */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            onClick={() => setMode('voice')}
            className="w-full py-4 px-6 rounded-2xl bg-gradient-to-r from-violet-600/90 to-purple-600/90 text-white font-medium flex items-center justify-center gap-3 hover:from-violet-500/90 hover:to-purple-500/90 transition-all shadow-lg shadow-purple-500/20"
            data-testid="talk-with-arvexis-btn"
          >
            <Mic className="w-5 h-5" />
            <span>{lang === 'es' ? 'Hablar con Arvexis' : 'Talk with Arvexis'}</span>
          </motion.button>
          
          {/* Secondary action: Chat with Arvexis */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            onClick={() => setMode('chat')}
            className="w-full py-3.5 px-6 rounded-2xl bg-white/5 border border-white/10 text-white/80 font-medium flex items-center justify-center gap-3 hover:bg-white/10 hover:text-white transition-all"
            data-testid="chat-with-arvexis-btn"
          >
            <MessageCircle className="w-5 h-5" />
            <span>{lang === 'es' ? 'Escribirle a Arvexis' : 'Chat with Arvexis'}</span>
          </motion.button>
        </div>
      </main>
      
      {/* Footer — subtle reminder */}
      <footer 
        className="relative z-10 py-6 text-center"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="text-white/30 text-xs tracking-wide"
        >
          {lang === 'es' ? 'Una conversación. Una compañera.' : 'One conversation. One companion.'}
        </motion.p>
      </footer>
      
      {/* Conversation History overlay */}
      <AnimatePresence>
        {showMemories && (
          <ConversationHistory
            lang={lang}
            onClose={() => setShowMemories(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArvexisPresenceScreen;
